/**
 * Экран Error Boxes (коробок с ошибками)
 * Вынесено из legacy-app.js для модульности
 */

import { logger } from "../../utils/logger.js";

/**
 * Создаёт менеджер для работы с лутбоксами
 */
/**
 * Показывает диалог подтверждения
 */
function showConfirm(message, telegramWebApp) {
  return new Promise((resolve) => {
    if (telegramWebApp && typeof telegramWebApp.showConfirm === 'function') {
      try {
        telegramWebApp.showConfirm(message, (result) => resolve(result === true));
      } catch (error) {
        console.warn('[showConfirm] error:', error);
        resolve(window.confirm ? window.confirm(message) : true);
      }
    } else {
      resolve(window.confirm ? window.confirm(message) : true);
    }
  });
}

export function createLootboxManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  showScreen = null,
  showPaymentScreen = null,
  documentRef = document
}) {
  let isLoading = false;
  let lootboxes = [];
  let dailyLootboxAvailable = false;
  let dailyLootboxNextAvailable = null; // Время следующего доступного лутбокса
  let dailyLootboxTimer = null; // Таймер обновления
  let openLootboxTimeout = null; // Для очистки таймера
  let lootboxNotificationTimeout = null; // Таймер для автоматического закрытия уведомления

  /**
   * Загружает типы лутбоксов из API
   */
  async function loadLootboxes() {
    if (isLoading) return;
    isLoading = true;

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/lootboxes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.success && response.data) {
        // response.data может быть объектом с полем data, или напрямую массивом
        let lootboxesData = response.data;
        
        // Если response.data - это объект с полем data (вложенная структура)
        if (lootboxesData && typeof lootboxesData === 'object' && !Array.isArray(lootboxesData) && lootboxesData.data) {
          lootboxesData = lootboxesData.data;
        }
        
        // Убеждаемся, что это массив
        if (Array.isArray(lootboxesData)) {
          lootboxes = lootboxesData;
        } else {
          logger.error('[LootboxManager] response.data не является массивом:', response.data);
          lootboxes = [];
        }
        
        // Проверяем ежедневный лутбокс ПЕРЕД рендерингом
        const authHeaders = buildAuthHeaders();
        if (authHeaders) {
          try {
            const dailyResponse = await safeFetch(`${apiUrl}/lootboxes/daily`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...authHeaders
              }
            });

            if (dailyResponse.success) {
              // safeFetch оборачивает ответ в { success: true, data: { ... } }
              // API возвращает { success: true, data: { canClaim, available, nextAvailableAt } }
              // Поэтому нужно проверить dailyResponse.data.data или dailyResponse.data
              let responseData = dailyResponse.data;
              
              logger.debug('[LootboxManager] Ежедневный лутбокс - полный ответ safeFetch:', JSON.stringify(dailyResponse, null, 2));
              
              // Если есть вложенная структура data.data (двойная вложенность)
              if (responseData?.data && typeof responseData.data === 'object') {
                responseData = responseData.data;
                logger.debug('[LootboxManager] Обнаружена вложенная структура data.data, используем:', responseData);
              }
              
              // Правильно обрабатываем ответ: canClaim - это boolean, который показывает доступность
              const canClaim = responseData?.canClaim ?? responseData?.available ?? false;
              dailyLootboxAvailable = canClaim;
              
              // Если лутбокс уже получен, вычисляем время до следующего доступного на клиенте
              const lastClaimDate = responseData?.lastClaimDate || null;
              if (!dailyLootboxAvailable && lastClaimDate) {
                // Вычисляем следующий доступный момент: завтра в 00:00:00 по московскому времени
                dailyLootboxNextAvailable = calculateNextAvailableTime(lastClaimDate);
                logger.debug('[LootboxManager] Вычислено время следующего доступного:', {
                  lastClaimDate,
                  nextAvailableAt: dailyLootboxNextAvailable
                });
              } else {
                dailyLootboxNextAvailable = null;
              }
              
              console.log('[LootboxManager] Ежедневный лутбокс - финальные значения:', {
                responseData: responseData,
                canClaim: responseData?.canClaim,
                available: responseData?.available,
                lastClaimDate,
                dailyLootboxAvailable,
                nextAvailableAt: dailyLootboxNextAvailable,
                willStartTimer: !dailyLootboxAvailable && dailyLootboxNextAvailable
              });
              
              // Запускаем таймер обновления, если лутбокс недоступен
              // Таймер работает на клиенте и обновляет отображение без запросов к серверу
              if (!dailyLootboxAvailable && dailyLootboxNextAvailable) {
                console.log('[LootboxManager] Запускаем таймер, т.к. лутбокс недоступен и есть nextAvailableAt');
                startDailyLootboxTimer();
              } else {
                console.log('[LootboxManager] Таймер не запускается:', {
                  dailyLootboxAvailable,
                  dailyLootboxNextAvailable,
                  reason: dailyLootboxAvailable ? 'лютбокс доступен' : 'нет nextAvailableAt'
                });
                stopDailyLootboxTimer();
              }
            } else {
              console.warn('[LootboxManager] Ошибка проверки ежедневного лутбокса:', dailyResponse);
              // При ошибке считаем, что лутбокс недоступен (безопасный вариант)
              dailyLootboxAvailable = false;
            }
          } catch (dailyError) {
            console.warn('[LootboxManager] Ошибка проверки ежедневного лутбокса:', dailyError);
            // При ошибке считаем, что лутбокс недоступен (безопасный вариант)
            dailyLootboxAvailable = false;
          }
        } else {
          // Если нет авторизации, считаем, что лутбокс недоступен
          dailyLootboxAvailable = false;
        }
        
        // Рендерим ПОСЛЕ обновления dailyLootboxAvailable
        renderLootboxes();
      } else {
        console.error('[LootboxManager] Ошибка загрузки лутбоксов:', response.error || response.message);
        lootboxes = [];
        renderLootboxes();
      }
    } catch (error) {
      console.error('[LootboxManager] Ошибка:', error);
    } finally {
      isLoading = false;
    }
  }

  /**
   * Открывает лутбокс
   */
  async function openLootbox(lootboxTypeCode) {
    if (isLoading) return;
    
    const telegramWebApp = getTelegramWebApp();
    const authHeaders = buildAuthHeaders();
    
    if (!authHeaders) {
      showLootboxNotification('🔒 Ошибка', 'Ошибка авторизации. Перезагрузите приложение.');
      return;
    }

    isLoading = true;

    try {
      const apiUrl = getApiUrl();
      console.log('[LootboxManager] Открытие лутбокса:', lootboxTypeCode);
      const response = await safeFetch(`${apiUrl}/lootboxes/open`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ lootboxTypeCode })
      });
      
      console.log('[LootboxManager] Ответ сервера (полный):', response);
      console.log('[LootboxManager] response.data:', response.data);
      console.log('[LootboxManager] response.data.data:', response.data?.data);
      console.log('[LootboxManager] response.data.rewards:', response.data?.rewards);
      console.log('[LootboxManager] response.data.data?.rewards:', response.data?.data?.rewards);
      
      // Проверяем, что ответ успешен
      // Также проверяем статус 409 (Conflict) - это может быть daily_lootbox_already_claimed
      if (response.status === 409 || (response.error === 'daily_lootbox_already_claimed') || (response.payload?.error === 'daily_lootbox_already_claimed')) {
        // Лутбокс уже получен - обновляем данные с сервера
        console.log('[LootboxManager] Лутбокс уже получен (409 Conflict), обновляем данные');
        closeLootboxOverlay();
        await loadLootboxes();
        showLootboxNotification('⏰ Уже получено', 'Вы уже получили ежедневный лутбокс сегодня. Вернитесь завтра!');
        isLoading = false;
        return; // Выходим, не обрабатываем дальше
      }
      
      if (response.success && response.data) {
        console.log('[LootboxManager] Полный ответ сервера (JSON):', JSON.stringify(response.data, null, 2));
        
        // Показываем награды
        // API возвращает: { success: true, data: { lootboxType, rewards, user } }
        // safeFetch оборачивает это в: { success: true, data: { success: true, data: { ... } } }
        let rewards = null;
        let lootboxType = null;
        let userData = null;
        
        // Проверяем вложенную структуру (response.data.data)
        if (response.data?.data) {
          rewards = response.data.data.rewards;
          lootboxType = response.data.data.lootboxType;
          userData = response.data.data.user;
        } else if (response.data?.rewards) {
          // Если структура плоская
          rewards = response.data.rewards;
          lootboxType = response.data.lootboxType;
          userData = response.data.user;
        }
        
        console.log('[LootboxManager] Извлечённые данные:', {
          rewards,
          lootboxType,
          userData,
          rewardsIsArray: Array.isArray(rewards),
          rewardsLength: rewards?.length
        });
        
        // Обновляем баланс пользователя
        if (userData) {
          const user = getCurrentUser();
          if (user) {
            const updatedUser = { ...user, ...userData };
            setCurrentUser(updatedUser);
            updateUserBalancesUI(updatedUser);
          } else {
            updateUserBalancesUI(userData);
          }
        }
        
        if (rewards && Array.isArray(rewards) && rewards.length > 0) {
          // Ежедневный лутбокс открыт - показываем награды
          showRewards(rewards, lootboxType);
        } else if (response.data?.data?.message || response.data?.message) {
          // Лутбокс добавлен в инвентарь - показываем кастомное уведомление
          const message = response.data?.data?.message || response.data?.message;
          showLootboxNotification('📦 Лутбокс добавлен', message + '\n\nОткройте лутбокс в разделе "Инвентарь"');
          closeLootboxOverlay();
          // Перезагружаем список лутбоксов
          await loadLootboxes();
        } else {
          console.error('[LootboxManager] Награды не получены в ответе:', {
            data: response.data,
            rewards: rewards,
            rewardsType: typeof rewards,
            isArray: Array.isArray(rewards)
          });
          showLootboxNotification('❌ Ошибка', 'Награды не получены. Попробуйте позже.');
          closeLootboxOverlay();
        }
        
        // Обновляем список лутбоксов (для ежедневного)
        if (lootboxTypeCode === 'daily_free_box') {
          // НЕ устанавливаем dailyLootboxAvailable = false здесь!
          // Перезагружаем данные с сервера - там будет правильное значение
          await loadLootboxes();
        }
      } else {
        // Обработка ошибок (включая 409 Conflict)
        const errorCode = response.error || response.payload?.error || response.data?.error;
        const errorMessageFromResponse = response.message || response.payload?.message || response.data?.message;
        
        console.log('[LootboxManager] Ошибка открытия лутбокса:', {
          error: errorCode,
          message: errorMessageFromResponse,
          status: response.status,
          fullResponse: response
        });
        
        if (errorCode === 'daily_lootbox_already_claimed' || response.status === 409) {
          // Лутбокс уже получен - обновляем данные с сервера
          console.log('[LootboxManager] Лутбокс уже получен, обновляем данные');
          closeLootboxOverlay();
          await loadLootboxes();
          showLootboxNotification('⏰ Уже получено', 'Вы уже получили ежедневный лутбокс сегодня. Вернитесь завтра!');
          return; // Выходим, не показываем общую ошибку
        }
        
        // Обработка других ошибок
        // Закрываем overlay при ошибке
        const overlay = documentRef.getElementById('errorbox-overlay');
        const boxElement = documentRef.getElementById('errorbox-box');
        if (overlay) {
          overlay.style.display = 'none';
          overlay.classList.remove('active');
        }
        if (boxElement) {
          boxElement.classList.remove('opening', 'opened');
        }
        
        let errorMessage = errorMessageFromResponse || 'Ошибка открытия коробки';
        
        if (errorCode === 'insufficient_coins') {
          errorMessage = 'Недостаточно монет';
        } else if (errorCode === 'insufficient_stars') {
          errorMessage = 'Недостаточно звёзд';
        } else if (errorCode === 'lootbox_type_not_found') {
          errorMessage = 'Тип лутбокса не найден';
        } else if (errorCode === 'lootbox_type_code_required') {
          errorMessage = 'Не указан тип лутбокса';
        } else if (response.payload?.error) {
          errorMessage = response.payload.message || response.payload.error;
        } else if (response.message) {
          errorMessage = response.message;
        }
        
        console.error('[LootboxManager] Ошибка открытия:', { 
          error: response.error, 
          message: response.message, 
          status: response.status,
          payload: response.payload 
        });
        
        showLootboxNotification('❌ Ошибка', errorMessage);
      }
    } catch (error) {
      console.error('[LootboxManager] Ошибка открытия лутбокса:', error);
      showLootboxNotification('❌ Ошибка', 'Ошибка открытия коробки. Попробуйте позже.');
    } finally {
      isLoading = false;
    }
  }

  /**
   * Вычисляет время следующего доступного лутбокса на основе даты последнего получения
   * @param {string} lastClaimDate - Дата последнего получения в формате YYYY-MM-DD
   * @returns {string} ISO строка времени следующего доступного лутбокса
   */
  function calculateNextAvailableTime(lastClaimDate) {
    if (!lastClaimDate) return null;
    
    try {
      // Парсим дату последнего получения (YYYY-MM-DD)
      const [year, month, day] = lastClaimDate.split('-');
      
      // Следующий доступный момент - завтра в 00:00:00 по московскому времени
      // Создаём строку для завтрашней даты
      const tomorrowDateStr = `${year}-${String(parseInt(month)).padStart(2, '0')}-${String(parseInt(day) + 1).padStart(2, '0')}`;
      
      // Создаём дату завтра в 00:00:00, интерпретируя как московское время
      // Используем простой подход: создаём дату в формате, который будет правильно интерпретирован
      // Для московского времени (UTC+3) нужно вычесть 3 часа из UTC
      const tomorrowUTC = new Date(`${tomorrowDateStr}T00:00:00Z`);
      const moscowOffset = 3 * 60 * 60 * 1000; // 3 часа в миллисекундах
      const nextAvailable = new Date(tomorrowUTC.getTime() - moscowOffset);
      
      console.log('[LootboxManager] Вычисление следующего доступного времени:', {
        lastClaimDate,
        tomorrowDateStr,
        tomorrowUTC: tomorrowUTC.toISOString(),
        nextAvailable: nextAvailable.toISOString()
      });
      
      return nextAvailable.toISOString();
    } catch (error) {
      console.error('[LootboxManager] Ошибка вычисления следующего доступного времени:', error);
      // Простой fallback: завтра в 00:00:00 UTC
      try {
        const [year, month, day] = lastClaimDate.split('-');
        const tomorrow = new Date(parseInt(year), parseInt(month) - 1, parseInt(day) + 1);
        tomorrow.setUTCHours(0, 0, 0, 0);
        return tomorrow.toISOString();
      } catch (fallbackError) {
        console.error('[LootboxManager] Ошибка fallback вычисления:', fallbackError);
        return null;
      }
    }
  }

  /**
   * Запускает таймер обновления ежедневного лутбокса
   */
  function startDailyLootboxTimer() {
    stopDailyLootboxTimer(); // Останавливаем предыдущий таймер, если есть
    
    if (!dailyLootboxNextAvailable) {
      console.log('[LootboxManager] Таймер не запущен: dailyLootboxNextAvailable отсутствует');
      return;
    }
    
    console.log('[LootboxManager] Запуск таймера ежедневного лутбокса:', {
      nextAvailableAt: dailyLootboxNextAvailable,
      now: new Date().toISOString()
    });
    
    const updateTimer = () => {
      const now = new Date().getTime();
      const nextAvailable = new Date(dailyLootboxNextAvailable).getTime();
      const timeLeft = nextAvailable - now;
      
      if (timeLeft <= 0) {
        // Время пришло - обновляем статус
        console.log('[LootboxManager] Время пришло, обновляем статус лутбокса');
        dailyLootboxAvailable = true;
        dailyLootboxNextAvailable = null;
        stopDailyLootboxTimer();
        renderLootboxes();
        // Перезагружаем данные с сервера
        loadLootboxes();
        return;
      }
      
      // Обновляем отображение таймера
      renderLootboxes();
      
      // Планируем следующее обновление
      // Если осталось больше часа - обновляем каждые 10 секунд
      // Если меньше часа - каждую секунду для точности
      const updateInterval = timeLeft > 3600000 ? 10000 : 1000;
      dailyLootboxTimer = setTimeout(updateTimer, updateInterval);
    };
    
    // Запускаем сразу
    updateTimer();
  }
  
  /**
   * Останавливает таймер обновления ежедневного лутбокса
   */
  function stopDailyLootboxTimer() {
    if (dailyLootboxTimer) {
      clearTimeout(dailyLootboxTimer);
      dailyLootboxTimer = null;
    }
  }
  
  /**
   * Форматирует время до следующего доступного лутбокса
   */
  function formatTimeUntilNext(nextAvailableAt) {
    if (!nextAvailableAt) {
      console.log('[LootboxManager] formatTimeUntilNext: nextAvailableAt отсутствует');
      return '';
    }
    
    try {
      const now = new Date().getTime();
      const nextAvailable = new Date(nextAvailableAt).getTime();
      
      // Проверяем, что дата валидна
      if (isNaN(nextAvailable)) {
        console.error('[LootboxManager] formatTimeUntilNext: невалидная дата', nextAvailableAt);
        return '';
      }
      
      const timeLeft = nextAvailable - now;
      
      console.log('[LootboxManager] formatTimeUntilNext:', {
        nextAvailableAt,
        now: new Date().toISOString(),
        nextAvailable: new Date(nextAvailable).toISOString(),
        timeLeft,
        timeLeftMs: timeLeft,
        timeLeftHours: Math.floor(timeLeft / (1000 * 60 * 60))
      });
      
      if (timeLeft <= 0) {
        console.log('[LootboxManager] Время истекло или уже прошло');
        return '';
      }
      
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      
      let formatted = '';
      if (hours > 0) {
        formatted = `${hours}ч ${minutes}м ${seconds}с`;
      } else if (minutes > 0) {
        formatted = `${minutes}м ${seconds}с`;
      } else {
        formatted = `${seconds}с`;
      }
      
      console.log('[LootboxManager] Отформатированное время:', formatted);
      return formatted;
    } catch (error) {
      console.error('[LootboxManager] Ошибка форматирования времени:', error, nextAvailableAt);
      return '';
    }
  }
  
  /**
   * Рендерит список лутбоксов
   */
  function renderLootboxes() {
    const container = documentRef.getElementById('errorboxes-list');
    if (!container) return;

    container.innerHTML = '';

    // Маппинг кодов лутбоксов на иконки и описания
    const lootboxInfo = {
      'daily_free_box': { icon: '🎁', badge: 'DAILY' },
      'glitch_box': { icon: '📦' },
      'error_box': { icon: '💥', badge: 'BEST' },
      'system_crash_box': { icon: '💀' }
    };

    // Убеждаемся, что lootboxes - это массив
    if (!Array.isArray(lootboxes)) {
      console.error('[LootboxManager] lootboxes не является массивом:', lootboxes);
      lootboxes = [];
    }

    // Если лутбоксов нет, показываем сообщение
    if (lootboxes.length === 0) {
      container.innerHTML = '<div style="padding: 20px; text-align: center; color: var(--tg-theme-hint-color);">Загрузка коробок...</div>';
      return;
    }

    lootboxes.forEach(lootbox => {
      const info = lootboxInfo[lootbox.code] || { icon: '📦' };
      const isDaily = lootbox.code === 'daily_free_box';
      const canOpen = isDaily ? dailyLootboxAvailable : true;

      if (isDaily) {
        console.log(`[LootboxManager] Рендеринг ежедневного лутбокса:`, {
          isDaily,
          dailyLootboxAvailable,
          canOpen,
          dailyLootboxNextAvailable,
          willShowAsAvailable: canOpen
        });
      }

      const boxEl = documentRef.createElement('div');
      boxEl.className = `errorbox-item ${!canOpen && isDaily ? 'disabled' : ''} ${isLoading ? 'loading' : ''}`;
      
      if (canOpen && !isLoading) {
        boxEl.style.cursor = 'pointer';
        // Ежедневные лутбоксы открываются сразу, остальные покупаются (добавляются в инвентарь)
        boxEl.onclick = () => buyLootboxFromStore(lootbox.code);
      } else {
        boxEl.style.cursor = 'not-allowed';
      }
      
      let priceHtml = '';
      if (lootbox.cost_type === 'free') {
        if (canOpen) {
          priceHtml = `<div class="errorbox-price free">БЕСПЛАТНО</div>`;
        } else {
          const timeLeft = formatTimeUntilNext(dailyLootboxNextAvailable);
          console.log('[LootboxManager] Рендеринг "УЖЕ ПОЛУЧЕНО":', {
            dailyLootboxNextAvailable,
            timeLeft,
            willShowTimer: !!timeLeft
          });
          
          if (timeLeft) {
            priceHtml = `<div class="errorbox-price free" style="display: flex; flex-direction: column; gap: 4px; align-items: center;">
              <div>УЖЕ ПОЛУЧЕНО</div>
              <div style="font-size: 11px; opacity: 0.8; margin-top: 2px;">Доступно через: ${timeLeft}</div>
            </div>`;
          } else {
            priceHtml = `<div class="errorbox-price free">
              <div>УЖЕ ПОЛУЧЕНО</div>
            </div>`;
          }
        }
      } else if (lootbox.cost_type === 'coins') {
        priceHtml = `<div class="errorbox-price coins">💰 ${lootbox.cost_amount}</div>`;
      } else if (lootbox.cost_type === 'stars') {
        priceHtml = `<div class="errorbox-price stars">⭐ ${lootbox.cost_amount}</div>`;
      }
      
      boxEl.innerHTML = `
        ${info.badge ? `<div class="errorbox-badge">${info.badge}</div>` : ''}
        <div class="errorbox-icon">${info.icon}</div>
        <div class="errorbox-name">${lootbox.name}</div>
        <div class="errorbox-desc">${lootbox.description || ''}</div>
        ${priceHtml}
      `;
      container.appendChild(boxEl);
    });
  }

  /**
   * Покупает лутбокс (добавляет в инвентарь, не открывает сразу)
   * Для ежедневных лутбоксов открывает сразу
   */
  async function buyLootboxFromStore(lootboxTypeCode) {
    const telegramWebApp = getTelegramWebApp();
    const authHeaders = buildAuthHeaders();
    
    if (!authHeaders) {
      showLootboxNotification('🔒 Ошибка', 'Ошибка авторизации. Перезагрузите приложение.');
      return;
    }

    // Ежедневные лутбоксы открываются сразу
    if (lootboxTypeCode === 'daily_free_box') {
      return handleOpenLootbox(lootboxTypeCode);
    }

    // Остальные лутбоксы покупаются (добавляются в инвентарь)
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/lootboxes/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ lootboxTypeCode })
      });

      if (response.success && response.data?.success) {
        const data = response.data;
        
        // Обновляем баланс пользователя
        const user = getCurrentUser();
        if (user && data.user) {
          const updatedUser = { ...user, ...data.user };
          setCurrentUser(updatedUser);
          updateUserBalancesUI(updatedUser);
        }
        
        showLootboxNotification('📦 Лутбокс добавлен', data.message || 'Лутбокс добавлен в инвентарь! Откройте его в разделе "Инвентарь"');
        
        // Перезагружаем список лутбоксов
        await loadLootboxes();
      } else {
        // При ошибке safeFetch возвращает данные в response.payload, а не в response.data
        // Также проверяем response.data на случай, если структура ответа другая
        const errorData = response.payload || response.data || {};
        const errorCode = errorData.error || response.error;
        let errorMessage = errorData.message || response.message || errorCode || 'Ошибка покупки лутбокса';
        
        console.log('[LootboxManager] Ошибка покупки:', { 
          errorCode, 
          errorMessage, 
          errorData, 
          payload: response.payload,
          data: response.data,
          error: response.error,
          status: response.status
        });
        
        // Обрабатываем ошибки недостатка валюты
        if (errorCode === 'insufficient_coins' || errorCode === 'insufficient_stars') {
          const currency = errorData.currency || (errorCode === 'insufficient_coins' ? 'coins' : 'stars');
          const currencyIcon = currency === 'coins' ? '💰' : '⭐';
          const currencyName = currency === 'coins' ? 'монет' : 'звёзд';
          const required = errorData.required || 0;
          const current = errorData.current || 0;
          const missing = errorData.missing || (required - current);
          
          // Используем сообщение из ответа или формируем своё
          const message = errorData.message || errorMessage || `Недостаточно ${currencyName}.\nТребуется: ${required} ${currencyIcon}\nУ вас: ${current} ${currencyIcon}\nНе хватает: ${missing} ${currencyIcon}`;
          
          // Предлагаем пополнить баланс
          const confirmed = await showConfirm(
            `${message}\n\nХотите пополнить баланс?`,
            telegramWebApp
          );
          
          if (confirmed) {
            // Переходим на экран покупки
            if (showPaymentScreen && typeof showPaymentScreen === 'function') {
              showPaymentScreen();
            } else if (showScreen && typeof showScreen === 'function') {
              showScreen('payment');
            } else if (typeof window.showPaymentScreen === 'function') {
              window.showPaymentScreen();
            } else if (typeof window.showScreen === 'function') {
              window.showScreen('payment');
            } else {
              showLootboxNotification('💳 Пополнение баланса', 'Перейдите в раздел "Платежи" для пополнения баланса');
            }
          } else {
            showLootboxNotification('❌ Недостаточно средств', message);
          }
        } else {
          // Для других ошибок просто показываем сообщение
          showLootboxNotification('❌ Ошибка', errorMessage);
        }
      }
    } catch (error) {
      console.error('[LootboxManager] Ошибка покупки лутбокса:', error);
      showLootboxNotification('❌ Ошибка', 'Ошибка покупки лутбокса. Попробуйте позже.');
    }
  }

  /**
   * Обрабатывает открытие лутбокса (для ежедневных или из инвентаря)
   * Экспортируется для использования в других местах (например, в магазине)
   */
  function handleOpenLootbox(lootboxTypeCode) {
    const overlay = documentRef.getElementById('errorbox-overlay');
    const boxElement = documentRef.getElementById('errorbox-box');
    const rewardsElement = documentRef.getElementById('errorbox-rewards');
    const continueBtn = documentRef.getElementById('errorbox-continue');
    
    if (!overlay || !boxElement) {
      console.error('[LootboxManager] Overlay или box element не найдены');
      return;
    }

    // Проверяем, можно ли открыть ежедневную коробку
    if (lootboxTypeCode === 'daily_free_box' && !dailyLootboxAvailable) {
      const telegramWebApp = getTelegramWebApp();
      showLootboxNotification('⏰ Уже получено', 'Вы уже получили ежедневный лутбокс сегодня');
      return;
    }

    console.log('[LootboxManager] Начинаем открытие лутбокса:', lootboxTypeCode);

    // Показываем overlay и запускаем анимацию
    overlay.style.display = 'flex';
    overlay.classList.add('active');
    
    // Анимация открытия коробки
    boxElement.classList.add('opening');
    boxElement.onclick = null; // Отключаем клик во время открытия
    
    // Открываем коробку через API
    // Очищаем предыдущий таймер, если есть
    if (openLootboxTimeout) {
      clearTimeout(openLootboxTimeout);
    }
    openLootboxTimeout = setTimeout(async () => {
      await openLootbox(lootboxTypeCode);
      openLootboxTimeout = null;
    }, 1000); // Задержка для анимации
  }

  /**
   * Показывает полученные награды
   */
  function showRewards(rewards, lootboxType) {
    const boxElement = documentRef.getElementById('errorbox-box');
    const rewardsElement = documentRef.getElementById('errorbox-rewards');
    const continueBtn = documentRef.getElementById('errorbox-continue');
    
    if (!rewardsElement || !boxElement) return;

    // Добавляем неоновую анимацию открытия
    boxElement.classList.remove('opening');
    boxElement.classList.add('neon-opening');
    
    // После анимации показываем награды
    setTimeout(() => {
      boxElement.classList.remove('neon-opening');
      boxElement.classList.add('neon-opened');
      
      // Ещё через немного показываем награды
      setTimeout(() => {
        showRewardsContent(rewards, lootboxType, rewardsElement, continueBtn);
      }, 300);
    }, 1000);
  }
  
  /**
   * Показывает контент с наградами
   */
  function showRewardsContent(rewards, lootboxType, rewardsElement, continueBtn) {
    
    // Проверяем, что rewards - это массив
    if (!rewards || !Array.isArray(rewards) || rewards.length === 0) {
      console.error('[LootboxManager] Награды не получены или не являются массивом:', rewards);
      rewardsElement.innerHTML = `
        <div class="rewards-title">⚠️ Ошибка получения наград</div>
        <div class="rewards-list">
          <div class="reward-item">Награды не получены</div>
        </div>
      `;
      rewardsElement.style.display = 'block';
      if (continueBtn) {
        continueBtn.style.display = 'block';
        continueBtn.onclick = () => closeLootboxOverlay();
      }
      return;
    }
    
    // Форматируем награды с красивым отображением
    const rewardsHtml = rewards.map((reward, index) => {
      let icon = '🎁';
      let name = 'Награда';
      let amount = '';
      let className = 'reward-item';
      
      if (reward.type === 'coins') {
        icon = '💰';
        name = 'Монеты';
        amount = reward.amount;
        className += ' coins';
      } else if (reward.type === 'stars') {
        icon = '⭐';
        name = 'Звёзды';
        amount = reward.amount;
        className += ' stars';
      } else if (reward.type === 'exp' || reward.type === 'experience') {
        icon = '📈';
        name = 'Опыт';
        amount = reward.amount;
        className += ' exp';
      } else if (reward.type === 'energy' || reward.type === 'power') {
        icon = '⚡';
        name = 'Энергия';
        amount = reward.amount;
        className += ' energy';
      } else if (reward.type === 'creature') {
        icon = '🦉';
        name = reward.name || 'Существо';
        amount = '';
        className += ' creature';
        if (reward.rarity) {
          className += ` rarity-${reward.rarity.toLowerCase()}`;
        }
      }
      
      return `
        <div class="${className}" style="animation-delay: ${index * 0.1}s;">
          <div class="reward-icon">${icon}</div>
          <div class="reward-name">${name}</div>
          ${amount ? `<div class="reward-amount">+${amount}</div>` : ''}
          ${reward.rarity ? `<div class="reward-rarity rarity ${reward.rarity.toLowerCase()}">${reward.rarity}</div>` : ''}
        </div>
      `;
    }).filter(html => html).join('');

    rewardsElement.innerHTML = `
      <div class="rewards-title">🎉 Награды получены!</div>
      <div class="rewards-subtitle">${lootboxType?.name || 'Из коробки'}</div>
      <div class="rewards-list">
        ${rewardsHtml}
      </div>
    `;
    
    rewardsElement.style.display = 'block';
    if (continueBtn) {
      continueBtn.style.display = 'block';
      continueBtn.onclick = () => closeLootboxOverlay();
    }
  }

  /**
   * Закрывает overlay с наградами
   */
  function closeLootboxOverlay() {
    // Очищаем таймер, если он активен
    if (openLootboxTimeout) {
      clearTimeout(openLootboxTimeout);
      openLootboxTimeout = null;
    }
    
    const overlay = documentRef.getElementById('errorbox-overlay');
    const boxElement = documentRef.getElementById('errorbox-box');
    const rewardsElement = documentRef.getElementById('errorbox-rewards');
    const continueBtn = documentRef.getElementById('errorbox-continue');
    
    if (overlay) {
      overlay.style.display = 'none';
      overlay.classList.remove('active');
    }
    
    if (boxElement) {
      boxElement.classList.remove('opening', 'opened');
      boxElement.onclick = null;
    }
    
    if (rewardsElement) {
      rewardsElement.style.display = 'none';
      rewardsElement.innerHTML = '';
    }
    
    if (continueBtn) {
      continueBtn.style.display = 'none';
      continueBtn.onclick = null;
    }
  }

  /**
   * Показывает кастомное уведомление о лутбоксе
   * @param {string} title - Заголовок уведомления
   * @param {string} message - Текст сообщения
   * @param {number} duration - Длительность показа в миллисекундах (по умолчанию 4000)
   */
  function showLootboxNotification(title, message, duration = 4000) {
    const notification = documentRef.getElementById('lootbox-notification');
    if (!notification) {
      console.warn('[LootboxManager] Элемент lootbox-notification не найден, используем fallback');
      const telegramWebApp = getTelegramWebApp();
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(message);
      }
      return;
    }
    
    const titleEl = notification.querySelector('[data-lootbox-notification-title]');
    const messageEl = notification.querySelector('[data-lootbox-notification-message]');
    
    if (titleEl) titleEl.textContent = title;
    if (messageEl) messageEl.textContent = message;
    
    notification.classList.remove('hidden');
    notification.classList.add('active');
    
    // Автоматически закрываем через duration
    if (lootboxNotificationTimeout) {
      clearTimeout(lootboxNotificationTimeout);
    }
    
    lootboxNotificationTimeout = setTimeout(() => {
      dismissLootboxNotification();
    }, duration);
  }
  
  /**
   * Скрывает уведомление о лутбоксе
   */
  function dismissLootboxNotification() {
    const notification = documentRef.getElementById('lootbox-notification');
    if (notification) {
      notification.classList.remove('active');
      notification.classList.add('hidden');
    }
    if (lootboxNotificationTimeout) {
      clearTimeout(lootboxNotificationTimeout);
      lootboxNotificationTimeout = null;
    }
  }
  
  /**
   * Загружает и отображает экран Error Boxes
   */
  function loadErrorBoxesScreen() {
    loadLootboxes();
  }

  /**
   * Получает список лутбоксов (для использования в других местах)
   */
  async function getLootboxesList() {
    await loadLootboxes();
    return {
      lootboxes,
      dailyLootboxAvailable
    };
  }
  
  // Экспортируем функции для глобального доступа
  if (typeof window !== 'undefined') {
    window.dismissLootboxNotification = dismissLootboxNotification;
  }

  // Экспортируем функции
  /**
   * Получает время следующего доступного ежедневного лутбокса
   * @returns {string|null} ISO строка времени или null
   */
  function getDailyLootboxNextAvailable() {
    return dailyLootboxNextAvailable;
  }
  
  return {
    loadErrorBoxesScreen,
    openLootbox,
    buyLootboxFromStore,
    closeLootboxOverlay,
    getLootboxesList,
    handleOpenLootbox,
    showLootboxNotification,
    dismissLootboxNotification,
    getDailyLootboxNextAvailable
  };
}

/**
 * Функция для обратной совместимости
 */
export function loadErrorBoxesScreen() {
  console.warn('[ErrorBoxes] loadErrorBoxesScreen вызван без менеджера. Используйте createLootboxManager.');
}

